import { ArrowLeft, Ban, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { XeroSyncJobLastRun } from '@ankor-io/common/types/integrations/xero/types'
import { XeroConnectionSyncStatus } from '@ankor-io/common/types/integrations/xero/types'
import {
  Badge,
  Button,
  CopyToClipboard,
  Loading,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import { useXeroConnectionsQuery, useXeroSyncJobsQuery } from '@/queries/organization'
import { formatDate, formatDuration } from '@/utils/format'

import { statusBadge } from './integration-status'

function lastRunIcon(lastRun?: XeroSyncJobLastRun) {
  if (!lastRun) return <Ban className="size-4 text-gray-400" />

  switch (lastRun.status) {
    case XeroConnectionSyncStatus.COMPLETED:
      return <CheckCircle2 className="size-4 text-green-500" />
    case XeroConnectionSyncStatus.FAILED:
      return <XCircle className="size-4 text-red-500" />
    case XeroConnectionSyncStatus.IN_PROGRESS:
      return <Loader2 className="size-4 text-yellow-500 animate-spin" />
    default:
      return <Ban className="size-4 text-gray-400" />
  }
}

export function IntegrationSyncJobsView({
  organizationId,
  onBack,
  onSelectJob,
  onSelectLastRun,
}: {
  organizationId: string
  onBack: () => void
  onSelectJob?: (jobId: string) => void
  onSelectLastRun?: (jobId: string, runId: string) => void
}) {
  const [vesselFilter, setVesselFilter] = useState<string>('all')
  const { data: connections, isPending: isConnectionsPending } = useXeroConnectionsQuery(organizationId)
  const connectionId = connections?.[0]?.id
  const { data: jobs, isPending: isJobsPending, error } = useXeroSyncJobsQuery(organizationId, connectionId)

  const isPending = isConnectionsPending || isJobsPending

  const vesselNames = useMemo(() => {
    if (!jobs) return []
    return [...new Set(jobs.map((j) => j.connectedAccountName).filter(Boolean))].sort()
  }, [jobs])

  const filteredJobs = useMemo(() => {
    if (!jobs) return []
    if (vesselFilter === 'all') return jobs
    return jobs.filter((j) => j.connectedAccountName === vesselFilter)
  }, [jobs, vesselFilter])

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            plain
            onClick={onBack}
            className="flex items-center"
          >
            <ArrowLeft data-slot="icon" />
            Back
          </Button>
          <span className="text-gray-300 dark:text-gray-600">/</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">Xero — Sync Jobs</span>
          {jobs && (
            <Badge
              color="gray"
              className="whitespace-nowrap"
            >
              {filteredJobs.length} jobs
            </Badge>
          )}
        </div>
        {jobs && jobs.length > 0 && (
          <Select
            className="w-auto max-w-xs"
            value={vesselFilter}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setVesselFilter(e.target.value)}
            aria-label="Filter by vessel"
          >
            <option value="all">All vessels</option>
            {vesselNames.map((name) => (
              <option
                key={name}
                value={name}
              >
                {name}
              </option>
            ))}
          </Select>
        )}
      </div>
      {isPending && <Loading variant="spinner" />}
      {error && <div className="text-sm text-red-500">Error: {error.message}</div>}
      {!isPending && connections && connections.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">No Xero connection found for this organization.</p>
      )}
      {jobs && jobs.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No sync jobs found.</p>}
      {jobs && jobs.length > 0 && (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Job</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Vessel</TableHeader>
                <TableHeader>Tenant</TableHeader>
                <TableHeader>Last Run</TableHeader>
                <TableHeader>Created At</TableHeader>
                <TableHeader>Duration</TableHeader>
                <TableHeader>Action</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.map((job) => (
                <TableRow
                  key={job.id}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  onClick={() => onSelectJob?.(job.id)}
                >
                  <TableCell className="font-mono text-sm">
                    <Badge color="gray">{job.id}</Badge>
                  </TableCell>
                  <TableCell>{statusBadge(job.status)}</TableCell>
                  <TableCell>{job.connectedAccountName || '—'}</TableCell>
                  <TableCell>{job.tenantName || '—'}</TableCell>
                  <TableCell>
                    {job.lastRun ? (
                      <button
                        className="hover:opacity-70 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelectLastRun?.(job.id, job.lastRun!.id)
                        }}
                      >
                        {lastRunIcon(job.lastRun)}
                      </button>
                    ) : (
                      lastRunIcon()
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums text-gray-500">{formatDate(job.startedAt)}</TableCell>
                  <TableCell className="tabular-nums text-gray-500">{formatDuration(job.duration)}</TableCell>
                  <TableCell>
                    <div onClick={(e) => e.stopPropagation()}>
                      <CopyToClipboard
                        text={job.id}
                        tooltip="Copy Job ID"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  )
}
